import React from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Divider,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupIcon from "@mui/icons-material/Group";
import StarIcon from "@mui/icons-material/Star";
import VerifiedIcon from "@mui/icons-material/Verified";
import { MemberInfo } from "../Creation/CreateGroupForm";
import DeleteConfirmation from "./DeleteConfirmation";
import { useDeleteMemberMutation } from "components/Slices/circleApiSlice";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { ImageAvatar } from "./ImageAvatar";
import InviteShareButtons from "./InviteShareButtons";

interface UserStatusModalProps {
  open: boolean;
  onClose: () => void;
  // inviteToken only arrives for the circle admin (backend strips it otherwise).
  members: {
    status: string;
    inviteToken?: string | null;
    User: MemberInfo & { status?: string };
  }[];
  group: any;
}

const statusConfig: Record<
  string,
  { label: string; color: "success" | "warning" | "error" | "default" }
> = {
  accepted: { label: "Accepted", color: "success" },
  pending: { label: "Awaiting Decision", color: "warning" },
  rejected: { label: "Declined", color: "error" },
  declined: { label: "Declined", color: "error" },
};

function UserStatusModal({
  open,
  onClose,
  members,
  group,
}: UserStatusModalProps) {
  const theme = useTheme();
  const user = useSelector((state: RootState) => state.auth.user);

  const [deleteMember] = useDeleteMemberMutation();
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleteUser, setDeleteUser] = React.useState<MemberInfo | null>(null);

  if (!user) return null;

  const handleDeleteMember = (u: MemberInfo) => {
    setDeleteUser(u);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteUser) {
      try {
        await deleteMember({
          groupId: group.id,
          userId: deleteUser.id,
        }).unwrap();
        setDeleteModalOpen(false);
      } catch (error) {
        console.error("Error deleting member:", error);
      }
    }
  };

  const acceptedCount = members.filter((m) => m.status === "accepted").length;
  const pendingCount = members.filter((m) => m.status === "pending").length;

  return (
    <>
      {deleteUser && (
        <DeleteConfirmation
          deleteConfirmOpen={deleteModalOpen}
          setDeleteConfirmOpen={setDeleteModalOpen}
          onConfirm={handleConfirmDelete}
          user={deleteUser}
        />
      )}

      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="user-management-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 520,
            maxWidth: "92vw",
            bgcolor: "background.paper",
            borderRadius: 4,
            boxShadow:
              "0 8px 32px rgba(16,24,64,0.18), 0 2px 8px rgba(16,24,64,0.1)",
            outline: "none",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 2.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <GroupIcon sx={{ fontSize: "1.2rem", color: "primary.main" }} />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                id="user-management-modal-title"
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: "text.primary",
                  lineHeight: 1.3,
                }}
              >
                Participants
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
                <Box
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: "success.dark",
                    }}
                  >
                    {acceptedCount} accepted
                  </Typography>
                </Box>
                {pendingCount > 0 && (
                  <Box
                    sx={{
                      px: 1,
                      py: 0.2,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "warning.dark",
                      }}
                    >
                      {pendingCount} pending
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: "text.secondary",
                bgcolor: alpha(theme.palette.text.secondary, 0.06),
                "&:hover": {
                  bgcolor: alpha(theme.palette.text.secondary, 0.12),
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Member list */}
          <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
            {members.length === 0 ? (
              <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
                <Typography
                  sx={{ color: "text.secondary", fontSize: "0.9rem" }}
                >
                  No participants yet.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {members.map(({ User, status, inviteToken }, index) => {
                  const isAdmin = group.adminUserID === User.id;
                  const isVerified = User.status?.toLowerCase() === "active";
                  const cfg = statusConfig[status] ?? {
                    label: status,
                    color: "default" as const,
                  };

                  return (
                    <Box key={User.id}>
                      <ListItem
                        sx={{
                          px: 3,
                          py: 1.75,
                          gap: 0,
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                          },
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: 48 }}>
                          <Box
                            sx={{ position: "relative", width: 36, height: 36 }}
                          >
                            <ImageAvatar
                              displayImg={User.displayImg ?? undefined}
                              firstName={User.firstName}
                              sx={{
                                width: 36,
                                height: 36,
                                fontSize: "0.85rem",
                              }}
                            />
                            {isAdmin && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: -2,
                                  right: -2,
                                  width: 16,
                                  height: 16,
                                  borderRadius: "50%",
                                  bgcolor: "primary.main",
                                  border: "2px solid",
                                  borderColor: "background.paper",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <StarIcon
                                  sx={{ fontSize: "0.78rem", color: "#fff" }}
                                />
                              </Box>
                            )}
                          </Box>
                        </ListItemAvatar>

                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.88rem",
                                  fontWeight: 600,
                                  color: "text.primary",
                                }}
                              >
                                {User.firstName} {User.lastName}
                              </Typography>
                              {isVerified && (
                                <VerifiedIcon sx={{ fontSize: "0.95rem", color: "primary.main" }} />
                              )}
                              {isAdmin && (
                                <Typography
                                  sx={{
                                    fontSize: "0.78rem",
                                    fontWeight: 700,
                                    color: "primary.main",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                  }}
                                >
                                  Admin
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                mt: 0.1,
                              }}
                            >
                              {User.email}
                            </Typography>
                          }
                        />

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            ml: 1,
                          }}
                        >
                          <Chip
                            label={cfg.label}
                            color={cfg.color}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                          {group.isAdmin && User.email !== user.email && (
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteMember(User)}
                              sx={{
                                color: "error.main",
                                opacity: 0.5,
                                "&:hover": {
                                  opacity: 1,
                                  bgcolor: alpha(
                                    theme.palette.error.main,
                                    0.08,
                                  ),
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          )}
                        </Box>
                      </ListItem>
                      {/* Shareable link for an open invite — admin only
                          (inviteToken is absent for everyone else). */}
                      {status === "pending" && inviteToken && (
                        <Box sx={{ pl: 9, pr: 3, pb: 1.75, mt: -0.75 }}>
                          <InviteShareButtons
                            token={inviteToken}
                            circleTitle={group.title}
                          />
                        </Box>
                      )}
                      {index < members.length - 1 && (
                        <Divider sx={{ ml: 7.5 }} />
                      )}
                    </Box>
                  );
                })}
              </List>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              onClick={onClose}
              variant="outlined"
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: "0.82rem",
                borderColor: "divider",
                color: "text.secondary",
                "&:hover": {
                  borderColor: "text.primary",
                  color: "text.primary",
                  bgcolor: "transparent",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default UserStatusModal;
